import type { PageResponse } from './common'

export type MaintenanceOrderStatus =
  | 'SCHEDULED'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'PENDING_CONFIRMATION'
  | 'COMPLETED'
  | 'CANCELLED'

export type MaintenanceType = 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL' | 'CUSTOM'
export type MaintenanceOverallResult = 'PASS' | 'ABNORMAL'
export type MaintenanceAttachmentType =
  | 'FORM_PAGE'
  | 'BEFORE_PHOTO'
  | 'AFTER_PHOTO'
  | 'FAULT_PHOTO'
  | 'SIGNATURE'
  | 'OTHER'

export const MAINTENANCE_STATUS_LABEL: Record<MaintenanceOrderStatus, string> = {
  SCHEDULED: '待安排',
  ASSIGNED: '已指派',
  IN_PROGRESS: '保养中',
  PENDING_CONFIRMATION: '待确认',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
}

export const MAINTENANCE_TYPE_LABEL: Record<MaintenanceType, string> = {
  QUARTERLY: '季度保养',
  SEMI_ANNUAL: '半年保养',
  ANNUAL: '年度保养',
  CUSTOM: '临时保养',
}

export const MAINTENANCE_ATTACHMENT_LABEL: Record<MaintenanceAttachmentType, string> = {
  FORM_PAGE: '保养单页面',
  BEFORE_PHOTO: '保养前照片',
  AFTER_PHOTO: '保养后照片',
  FAULT_PHOTO: '异常照片',
  SIGNATURE: '签字页',
  OTHER: '其他附件',
}

export interface MaintenanceAttachment {
  id: number
  fileAssetId: number
  attachmentType: MaintenanceAttachmentType
  fileRef: string
  originalFilename: string
  contentType?: string
  fileSize?: number
  checksum?: string
  pageNo?: number
  remark?: string
  uploadedBy?: string
  uploadedAt: string
}

export interface MaintenanceActionLog {
  id: number
  actionType: string
  fromStatus?: string
  toStatus?: string
  operatorId: string
  operatorName: string
  operatorRole: string
  remark?: string
  createdAt: string
}

export interface LinkedRepairOrder {
  id: number
  orderNo: string
  title: string
  status: string
  createdAt: string
}

export interface MaintenanceOrder {
  id: number
  orderNo: string
  planId?: number
  contractId?: number
  contractDeviceId?: number
  deviceId: number
  customerId?: number
  customerName?: string
  hospitalName?: string
  department?: string
  installLocation?: string
  deviceName: string
  deviceModel?: string
  deviceSerialNo?: string
  manufacturer?: string
  maintenanceType: MaintenanceType
  scheduledDate: string
  assignedEngineerId?: string
  assignedEngineerName?: string
  status: MaintenanceOrderStatus
  overdue: boolean
  startedAt?: string
  submittedAt?: string
  completedAt?: string
  formMode: 'PHOTO' | 'HYBRID' | 'STRUCTURED'
  formName: string
  formVersion?: string
  formPageCount?: number
  overallResult?: MaintenanceOverallResult
  issueSummary?: string
  actionSummary?: string
  signatureConfirmed: boolean
  customerRating?: number
  confirmationRemark?: string
  cancelReason?: string
  createdBy?: string
  createdAt: string
  updatedAt: string
  attachments: MaintenanceAttachment[]
  actions: MaintenanceActionLog[]
  linkedRepairOrders: LinkedRepairOrder[]
}

export type MaintenanceOrderPageResponse = PageResponse<MaintenanceOrder>

export interface MaintenanceOrderFilter {
  status?: MaintenanceOrderStatus
  keyword?: string
  engineerId?: string
  scheduledFrom?: string
  scheduledTo?: string
  page?: number
  size?: number
}

export interface CreateMaintenanceOrderRequest {
  planId?: number
  contractId?: number
  contractDeviceId?: number
  deviceId: number
  maintenanceType: MaintenanceType
  scheduledDate: string
  formName: string
  formVersion?: string
  assignedEngineerId?: string
  assignedEngineerName?: string
}

export interface FileAssetUploadResponse {
  id: number
  fileCode: string
  objectKey: string
  originalFilename: string
  contentType?: string
  fileSize: number
  checksumSha256?: string
  accessUrl?: string
}
